import DOMPurify from 'dompurify';

/**
 * Sanitizes user-generated HTML content to prevent XSS attacks
 * 
 * @param {string} content - Content to sanitize
 * @returns {string} - Sanitized content
 */
export const sanitizeHtml = (content) => {
  if (!content || typeof content !== 'string') {
    return '';
  }
  return DOMPurify.sanitize(content, {
    FORBID_TAGS: ['script', 'style', 'iframe', 'frame', 'object', 'embed', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'eval'],
    ALLOW_DATA_ATTR: false
  });
};

/**
 * Performs enhanced URL validation beyond basic format checking
 * 
 * @param {string} url - URL to validate
 * @returns {boolean} - Whether URL is valid and safe
 */
export const validateUrl = (url) => {
  // Basic format validation
  try {
    const urlObj = new URL(url);
    
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(urlObj.protocol.toLowerCase())) {
      return false;
    }
    
    // Block localhost and private IPs
    const hostname = urlObj.hostname.toLowerCase();
    const privateHostPatterns = [
      /^localhost$/,
      /^127\.\d+\.\d+\.\d+$/,
      /^10\.\d+\.\d+\.\d+$/,
      /^172\.(1[6-9]|2\d|3[0-1])\.\d+\.\d+$/,
      /^192\.168\.\d+\.\d+$/,
      /^0\.0\.0\.0$/,
      /\.local$/,
      /\.internal$/,
      /\.intranet$/,
      /\.corp$/,
      /\.lan$/
    ];
    
    if (privateHostPatterns.some(pattern => pattern.test(hostname))) {
      return false;
    }
    
    // Check for potentially dangerous URL parts
    const fullUrl = url.toLowerCase();
    // List of dangerous URL protocol prefixes
    const dangerousProtocols = [
      'javascript',
      'data',
      'vbscript',
      'file',
      'about',
      'gopher',
      'ftp'
    ];
    
    // Check if URL contains dangerous protocols or patterns
    if (
      dangerousProtocols.some(protocol => fullUrl.indexOf(protocol + ':') !== -1) ||
      fullUrl.includes('@') ||
      fullUrl.includes(':///')
    ) {
      return false;
    }

    // URL is valid and passes security checks
    return true;
    
  } catch (e) {
    return false;
  }
};

/**
 * Safely display untrusted text content (not as HTML)
 * 
 * @param {string} text - Text to display safely
 * @returns {string} - Safe text for display
 */
export const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  
  // Simply replace HTML special characters
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}; 